/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Thu Jan 18 2024
 **/

import { omit, has } from "lodash-es";
import { v4 } from "uuid";

import { Configure } from "@/frameworks/libraries/utilities";

//*
import { XmsClient } from "../engine";
/*/
import * as Xms from "@rfice/xms";
//*/

const LOGTAG = "#XMSClientManager#";

class XMSClientManagerStatic {
	//*
	private _clients: Record<string, XmsClient> = {};
	/*/
	private _clients: Record<string, Xms.Client> = {};
	//*/
	private _configure: XmsClient.Configure = {
		mediaDevice: "CAMERA",
		resolution: 720,
		screenResolution: 1080
	};

	constructor() {
		this._configure.mediaDevice = "CAMERA";
		this._configure.resolution = 720;
	}

	/**
	 * create a new XMSClient instance
	 * @returns index of new instance
	 */
	public async join(
		roomID: string,
		userID: string,
		handler: XmsClient.ClientHandler,
		isVoiceCallMode = false,
		config: XmsClient.Configure | null = null
	) {
		const room = this.get(roomID);
		//*
		this._clients[roomID] = room ?? new XmsClient(null);
		/*/
		this._clients[roomID] = room ?? new Xms.Client();
		//*/
		if (!room) {
			await this._clients[roomID].initialize({
				configure: isVoiceCallMode
					? {
							mediaDevice: "MIC-ONLY",
							resolution: 720,
							screenResolution: 1080
						}
					: config ?? this._configure,
				//*
				onLocalStream: null,
				actorId: userID,
				roomId: roomID,
				userId: userID,
				/*/
				roomID,
				userID,
				actorID: userID,
				//*/
				handler
			});
		}
		//*
		await this._clients[roomID].join(roomID, userID);
		/*/
		await this._clients[roomID].start();
		//*/
		return this._clients[roomID];
	}

	public leave(roomID: string) {
		const instance = this.get(roomID);
		if (instance) {
			//*
			instance.leave();
			/*/
			instance.stop();
			//*/
			delete this._clients[roomID];
		}
	}

	public changeTalkingGroup(roomID: string, userList: string[]) {
		const instance = this.get(roomID);
		console.debug(LOGTAG, "talking group room", roomID, userList, instance);
		if (instance && userList.length > 0) {
			//*
			instance.changeTalkingGroup(userList); //채팅 룸에 있는 유저 id 추가
			/*/
			instance.change_group(userList);
			//*/
		}
	}

	public get(roomID: string) {
		return (has(this._clients, roomID) && this._clients[roomID]) || null;
	}

	public videoState(clientID: string, enable: boolean) {
		//*
		this.get(clientID)?.enableWebcam(enable);
		/*/
		this.get(clientID)?.video_enable(enable);
		//*/
	}

	public audioMute(roomID: string, isMuted: boolean) {
		this.get(roomID)?.mute(isMuted);
	}

	public startRecording(roomID: string, recordingFilename: "call" | "voicechat") {
		const seqNum = v4();
		Configure.recordingSeqNumList = {
			...Configure.recordingSeqNumList,
			[roomID]: { seqNum }
		};

		this.get(roomID)?.startRecording({ seqNum, recordingFilename });
	}

	public async startAudiozoneRecording(roomID: string, recordingFilename: "audiozone" | "meeting") {
		const seqNum = v4();
		Configure.recordingSeqNumList = {
			...Configure.recordingSeqNumList,
			[roomID]: { seqNum }
		};

		this._clients[roomID] = new XmsClient(roomID);
		await this.get(roomID)?.initialize({
			configure: this._configure,
			onLocalStream: null,
			actorId: Configure.userID,
			roomId: roomID,
			userId: Configure.userID,
			handler: null
		});
		this.get(roomID)?.startRecording({ seqNum, recordingFilename });
	}
	public stopRecording(roomID: string, isAudioZone?: boolean) {
		//*
		this.get(roomID)?.stopRecording({ seqNum: Configure.recordingSeqNumList[roomID].seqNum });
		/*/
		this.get(roomID)?.stop_recording({ seqNum: Configure.recordingSeqNumList[roomID].seqNum });
		//*/
		Configure.recordingSeqNumList = {
			...omit(Configure.recordingSeqNumList, roomID)
		};
		if (isAudioZone) {
			this.get(roomID)?.leave();
			delete this._clients[roomID];
		}
	}

	public startScreenShare(roomID: string, callback?: (stream: MediaStream | null) => void) {
		//*
		this.get(roomID)?.startScreenSharing(callback);
		/*/
		this.get(roomID)?.start_screen_share(callback);
		//*/
	}
	public stopScreenShare(roomID: string) {
		//*
		this.get(roomID)?.stopScreenSharing();
		/*/
		this.get(roomID)?.stop_screen_share();
		//*/
	}
}

const XMSClientManager = Object.freeze(new XMSClientManagerStatic());
export default XMSClientManager;
